import React, {useState} from 'react';
import {Form as FormikForm, Formik, FormikHelpers, FormikValues} from 'formik';
import IconLeft from '../icon/IconLeft';
import UIInput from '../../ui/form/UIInput';
import RadioGroup from '../../ui/RadioGroup';
import UIWysiwig, {WysiwigButtons} from '../../ui/form/UIWysiwig';
import PrimaryButton from '../../ui/PrimaryButton';
import TextButton from '../../ui/TextButton';
import {ButtonColour} from '../../ui/ButtonColour';
import IconTrash from '../icon/IconTrash';
import {
  BeforeAfter,
  EmailNotificationTemplate,
  EmailNotificationTemplateInput,
  EmailTemplateType,
  PlatformEventKind,
  TimeUnit
} from '../../schema';
import If from '../utils/If';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import {useMutation} from '@apollo/client';
import {handleError, handleSuccess} from '../../utils/Apollo';
import cn from 'classnames';
import classNames from 'classnames';

type Props = {
  objectId: string
  objectType: EmailTemplateType,
  allowedEvents: PlatformEventKind[]
  refetchQueries?: string[]
  template?: EmailNotificationTemplate
  onSuccess: () => void
  onCancel?: () => void
  dark?: boolean
  editableSettings?: boolean
  deletable?: boolean
}

export default function EmailNotification({
                                            objectId,
                                            objectType,
                                            allowedEvents,
                                            template,
                                            onSuccess,
                                            onCancel,
                                            refetchQueries,
                                            dark = false,
                                            editableSettings = true,
                                            deletable = true
                                          }: Props) {
  const [open, setOpen] = useState(!template);
  const [unitType, setUnitType] = useState(TimeUnit.Day);
  const [beforeAfter, setBeforeAfter] = useState(template?.conditions.when.beforeAfter || BeforeAfter.Before);
  const [eventType, setEventType] = useState(template?.conditions.event || allowedEvents[0]);
  const [editMode, setEditMode] = useState(!template);
  const [isDeleting, setIsDeleting] = useState(false);

  const [submitOnCreate] = useMutation(createNotification, {
    refetchQueries: refetchQueries
  });
  const [submitOnUpdate] = useMutation(updateNotification, {
    refetchQueries: refetchQueries
  });
  const [submitOnDelete] = useMutation(deleteNotification, {
    refetchQueries: refetchQueries
  });


  const onCreateUpdate = (values: FormikValues, helpersReact: FormikHelpers<FormikValues>) => {
    const data: EmailNotificationTemplateInput = {
      name: values.name,
      subject: values.subject,
      body: values.body,
      conditions: {
        event: eventType,
        when: {
          units: parseInt(values.units),
          unitType,
          beforeAfter
        }
      }
    };
    const submit = template?.id ? submitOnUpdate({
      variables: {
        id: template.id,
        objectType,
        objectId,
        data
      }
    }) : submitOnCreate({variables: {objectId, objectType, data}});
    helpersReact.setSubmitting(true);
    submit.then(
      () => {
        setEditMode(false);
        setOpen(false);
        handleSuccess(`Email template "${values.name}" was saved`);
        onSuccess();
      },
      (apolloError) => handleError(apolloError)
    ).finally(() => helpersReact.setSubmitting(false));
  };

  const onDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!deletable) {
      return;
    }
    e.preventDefault();
    if (template?.id) {
      setIsDeleting(true);
      submitOnDelete({
        variables: {objectId, objectType, id: template?.id}
      }).then(
        () => {
          setOpen(false);
          handleSuccess('Email template was deleted');
        },
        (apolloError) => handleError(apolloError)
      ).finally(() => setIsDeleting(false));
    }
  }


  const handleOpen = () => {
    if (template) {
      setOpen(!open);
    }
    setEditMode(false);
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    if (template) {
      setOpen(false);
      setEditMode(false)
    }
  };

  const initialValues = {
    name: template?.name || '',
    subject: template?.subject || '',
    body: template?.body || '',
    units: template?.conditions.when.units == undefined ? 1 : template?.conditions.when.units
  };
  const validation = Yup.object({
    name: Yup.string().required('Required'),
    subject: Yup.string().required('Required'),
    body: Yup.string().required('Required'),
    units: Yup.number().required('Required').min(0, 'Must be >= 0')
  });

  const getEventName = (eventKind?: PlatformEventKind) => {
    switch (eventKind) {
      case PlatformEventKind.Attendee_Registered:
        return 'attendee registers';
      case PlatformEventKind.Event_Started:
        return 'event starts';
      case PlatformEventKind.Event_Finished:
        return 'event ends';
      case PlatformEventKind.Evaluation_Created:
        return 'evaluation created';
      case PlatformEventKind.Certificate_Generated:
        return 'certificate generated';
      default:
        return 'license expires';
    }
  }

  const sendingConditions = () => {
    let when = `${template?.conditions.when.units} ${template?.conditions.when.unitType.toLocaleLowerCase()}s`
    if (template?.conditions.when.units === 0) {
      when = 'right'
    }
    const event = getEventName(template?.conditions.event);
    let whom;
    switch (template?.conditions.event) {
      case PlatformEventKind.Attendee_Registered:
      case PlatformEventKind.Event_Started:
      case PlatformEventKind.Event_Finished:
      case PlatformEventKind.Certificate_Generated:
      case PlatformEventKind.Evaluation_Created:
        whom = 'attendee';
        break;
      default:
        whom = 'trainer';
    }
    return (
      <>
        Notify {whom} {when}&nbsp;{template?.conditions.when.beforeAfter.toLocaleLowerCase()} {event}
      </>
    )
  }


  return (
    <div className={classNames('py-5 rounded-xl px-4 mt-3', {'bg-white': !dark, 'bg-gray-light': dark})}>
      <div className="flex justify-between cursor-pointer" onClick={handleOpen}>
        <div>
          <strong>{template?.name || "New notification"}</strong>
        </div>
        <If condition={!!template}>
          <span onClick={handleOpen} className="cursor-pointer">
            <IconLeft className={cn('transform', {'rotate-90': open, '-rotate-90': !open})}/>
          </span>
        </If>
      </div>
      <If condition={!editMode}>
        <span className="text-gray-dark">{sendingConditions()}</span>
        <If condition={!editMode && open && editableSettings}>
          <span className="ml-2 py-0 cursor-pointer text-blue" onClick={() => setEditMode(true)}>Edit settings</span>
        </If>
      </If>
      <If condition={open}>
        <Formik initialValues={initialValues} validationSchema={validation} onSubmit={onCreateUpdate}>
          {({isSubmitting}) =>
            <FormikForm>
              <UIInput name="name" id="name" placeholder="Enter the name of notification"
                       className={cn({'hidden': !editMode})}/>
              <div className={cn('flex pt-3 mb-4 justify-between', {'hidden': !editMode})}>
                <div className="flex">
                  <div className="w-16 mr-2">
                    <UIInput name="units" id="units" className="pt-3"/>
                  </div>
                  <div className="flex flex-col">
                    <div className="text-sm text-gray-dark">Period</div>
                    <RadioGroup
                      options={[{id: 'Hour', value: 'Hour'}, {id: 'Day', value: 'Day'}, {id: 'Month', value: 'Month'}]}
                      selectedValue={template?.conditions.when.unitType || unitType}
                      onChange={(value) => setUnitType(value as TimeUnit)}/>
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="text-sm text-gray-dark">When</div>
                  <RadioGroup options={[{id: 'Before', value: 'Before'}, {id: 'After', value: 'After'}]}
                              selectedValue={template?.conditions.when.beforeAfter || beforeAfter}
                              onChange={(value) => setBeforeAfter(value as BeforeAfter)}/>
                </div>
                <div className="flex flex-col w-44">
                  <div className="text-sm text-gray-dark">Event</div>
                  <If condition={editMode}>
                    <select onChange={(value) => setEventType(value.target.value as PlatformEventKind)}>
                      {allowedEvents.map((event) => (
                        <option key={event} value={event} selected={event === eventType}>{getEventName(event)}</option>
                      ))}
                    </select>
                  </If>
                  <If condition={!editMode}>
                    <span
                      className="border border-gray rounded-md bg-gray-light py-2 px-2">{getEventName(template?.conditions.event)}</span>
                  </If>
                </div>
              </div>
              <div className="mt-10 mb-2">
                <strong>Template</strong><br/>
                <UIInput name="subject" id="subject" placeholder="Subject"/>
              </div>
              <UIWysiwig name="body" id="body" minHeight={250} buttons={WysiwigButtons.default().html()}/>
              <div className="flex mt-3 justify-between">
                <div className="flex">
                  <PrimaryButton className="mr-3" loading={isSubmitting}>Save</PrimaryButton>
                  {/*<TextButton className="mr-3">Send test email</TextButton>*/}
                  <TextButton className="mr-3" onClick={handleCancel}>Cancel</TextButton>
                </div>
                <If condition={deletable}>
                  <TextButton className="text-gray-dark" colour={ButtonColour.RED} onClick={(e) => onDelete(e)}>
                    <If condition={isDeleting}>Deleting...</If>
                    <If condition={!isDeleting}>
                      <IconTrash className="mt-0.5"/> Delete notification
                    </If>
                  </TextButton>
                </If>
              </div>
            </FormikForm>
          }
        </Formik>
      </If>
    </div>
  )
}

const createNotification = gql`
  mutation CreateNotification($objectId: ID!, $objectType: EmailTemplateType!, $data: EmailNotificationTemplateInput!) {
    addEmailTemplate(objectId: $objectId, objectType: $objectType, data: $data) {
      id
      name
      subject
      body
      conditions {
        when {
          units
          unitType
          beforeAfter
        },
        event
      }
    }
  }
`;

const updateNotification = gql`
  mutation UpdateNotification($objectId: ID!, $objectType: EmailTemplateType!, $id: ID!, $data: EmailNotificationTemplateInput!) {
    updateEmailTemplate(objectId: $objectId, objectType: $objectType, id: $id, data: $data) {
      name
      subject
      body
      conditions {
        when {
          units
          unitType
          beforeAfter
        }
      }
    }
  }
`;

const deleteNotification = gql`
  mutation DeleteEmailNotification($objectId: ID!, $objectType: EmailTemplateType!, $id: ID!) {
    deleteEmailTemplate(objectId: $objectId, objectType: $objectType, id: $id) {
      id
    }
  }
`;
